/* prettier-ignore-file */
import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Switch,
  ActivityIndicator,
} from "react-native";
import { useNavigate } from "react-router-dom";
import AuthHeader from "../components/AuthHeader";
import { db, auth } from "../firebase.config";
import { getCollectionPath } from "../utils/dbPaths";
import { useUsersStore, useAuthStore } from "../stores";
import { getTenantId, getStoreId } from "../services/userStoreCache";
import {
  doc,
  setDoc,
  serverTimestamp,
  writeBatch,
  deleteField,
} from "firebase/firestore";
import { getSettingsPath } from "../utils/dbPaths";
import { getSetting } from "../services/settingsCache";
import {
  MIN_PIN_LENGTH,
  MAX_PIN_LENGTH,
  DEFAULT_PIN_LENGTH,
  ADMIN_PIN_LENGTH,
  ADMIN_AUTH_LEVEL,
  PIN_LENGTH_SETTING_DOC,
  PIN_LENGTH_SETTING_FIELD,
  clampPinLength,
  isAdminUser,
  pinLengthForAuthLevel,
  normalizePin,
} from "../utils/pinConfig";
import {
  registerForNotifications,
  disableNotifications,
  areNotificationsEnabled
} from "../services/fcmService";
import { formatPhoneNumber, formatPhoneNumberProgressive } from "../utils/formatting";
import { colors, radius, spacing, elevation } from "../theme";

// react-native-web ships Alert.alert() as an empty static method, so every
// Alert on this page was silently doing nothing. Route messages via the browser.
const notify = (title, message) => window.alert(message ? `${title}\n\n${message}` : title);

const UserSettings = () => {
  const navigate = useNavigate();
  const { users, currentUser } = useUsersStore();
  const { user: authUser, isAdmin, selectedUser } = useAuthStore();
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Desktop detection
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  
  // Form state
  const [userId, setUserId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [authLevel, setAuthLevel] = useState(1);
  const [pin, setPin] = useState("");
  const [phone, setPhone] = useState("");
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);

  // Store-wide PIN length for regular users. `pending` is what the stepper shows;
  // it only becomes `store` once Apply runs, because applying wipes every PIN.
  const [storePinLength, setStorePinLength] = useState(DEFAULT_PIN_LENGTH);
  const [pendingPinLength, setPendingPinLength] = useState(DEFAULT_PIN_LENGTH);
  // Raw text of the box, kept alongside the number so a two-digit value can be
  // typed a character at a time without "1" being clamped to the minimum first.
  const [pinLengthText, setPinLengthText] = useState(String(DEFAULT_PIN_LENGTH));
  const [pinLengthLoading, setPinLengthLoading] = useState(true);
  const [pinLengthSaving, setPinLengthSaving] = useState(false);

  const applyPendingPinLength = (value) => {
    const clamped = clampPinLength(value);
    setPendingPinLength(clamped);
    setPinLengthText(String(clamped));
  };

  // Edit and create users enabled for all users on this page
  const canEdit = true;

  // Everyone signs in through one shared Firebase account, so the `admin` claim
  // can't tell staff apart — the signed-in user's own authLevel is the real role.
  // The claim is still honoured so a genuine claim-admin is never locked out.
  const canManagePinLength = !!isAdmin || isAdminUser(selectedUser);

  // Length required by whoever is currently in the form. Reads the form's
  // authLevel rather than the stored one so promoting a user to admin flips the
  // requirement to 4 immediately.
  const requiredPinLength = pinLengthForAuthLevel(authLevel, storePinLength);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const value = await getSetting(
        PIN_LENGTH_SETTING_DOC,
        PIN_LENGTH_SETTING_FIELD,
        DEFAULT_PIN_LENGTH
      );
      if (cancelled) return;
      const clamped = clampPinLength(value);
      setStorePinLength(clamped);
      applyPendingPinLength(clamped);
      setPinLengthLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Stored PINs are padded to the width their owner's role requires — see
  // normalizePin() for why legacy numeric values need this.
  const pinForForm = (userData) =>
    normalizePin(
      userData?.pin,
      pinLengthForAuthLevel(userData?.authLevel, storePinLength)
    );

  const handleApplyPinLength = async () => {
    const next = clampPinLength(pendingPinLength);
    if (next === storePinLength) return;

    // Only PINs whose required width actually changes become invalid. Admins are
    // fixed at ADMIN_PIN_LENGTH, so the regular-user setting cannot invalidate
    // them and wiping them would just lock the store's admins out for nothing.
    const affected = Object.values(users || {}).filter(
      (u) =>
        pinLengthForAuthLevel(u.authLevel, storePinLength) !==
        pinLengthForAuthLevel(u.authLevel, next)
    );
    const adminCount = Object.values(users || {}).length - affected.length;

    const confirmed = window.confirm(
      `Set the PIN length for regular users to ${next} digits?\n\n` +
        `This resets (empties) the PIN of ${affected.length} regular user${
          affected.length === 1 ? "" : "s"
        }. You will have to set each one again by hand on this page before that ` +
        `person can sign in.\n\n` +
        `Admins (auth level ${ADMIN_AUTH_LEVEL}) are NOT affected — they stay at ` +
        `${ADMIN_PIN_LENGTH} digits and keep their PINs${
          adminCount ? ` (${adminCount} kept)` : ""
        }.\n\n` +
        `Anyone already signed in on this device stays signed in; a reset user ` +
        `cannot get past the PIN screen until their new PIN is set.\n\n` +
        `Continue?`
    );
    if (!confirmed) return;

    setPinLengthSaving(true);
    try {
      const settingsRef = doc(db, getSettingsPath(), PIN_LENGTH_SETTING_DOC);
      await setDoc(
        settingsRef,
        { [PIN_LENGTH_SETTING_FIELD]: next, updatedAt: serverTimestamp() },
        { merge: true }
      );

      // deleteField() rather than "" — the login check is `if (userPin && ...)`,
      // so an unset field reads as "no PIN" while an empty string would not.
      // Both spellings are removed because the login path reads `pin || PIN`.
      const usersPath = getCollectionPath("users");

      let batch = writeBatch(db);
      let opsInBatch = 0;
      let cleared = 0;
      for (const user of affected) {
        if (!user?.id) continue;
        batch.update(doc(db, usersPath, user.id), {
          pin: deleteField(),
          PIN: deleteField(),
          updatedAt: serverTimestamp(),
        });
        cleared += 1;
        opsInBatch += 1;
        if (opsInBatch === 450) { // Firestore caps a batch at 500 writes
          await batch.commit();
          batch = writeBatch(db);
          opsInBatch = 0;
        }
      }
      if (opsInBatch > 0) await batch.commit();

      setStorePinLength(next);
      setPin("");
      notify(
        "PIN length updated",
        `Regular users now use ${next} digits; admins keep their ${ADMIN_PIN_LENGTH}-digit PINs.\n\n` +
          `${cleared} PIN${cleared === 1 ? "" : "s"} cleared — set each one again by hand.`
      );
    } catch (error) {
      console.error("Error updating PIN length:", error);
      notify("Error", `Failed to update PIN length: ${error.message}`);
      applyPendingPinLength(storePinLength);
    } finally {
      setPinLengthSaving(false);
    }
  };

  // Get users list for dropdown - only show first/last name, no user ID
  const usersList = Object.values(users).map(user => ({
    id: user.id,
    name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.id,
    ...user
  }));

  const getPhoneDisplay = (userData) => {
    if (!userData?.phone) return "";
    const digits = String(userData.phone).replace(/\D/g, "").slice(0, 10);
    return digits.length === 10 ? formatPhoneNumber(digits) : digits;
  };

  // Load current user data on mount
  useEffect(() => {
    if (authUser && authUser.uid) {
      const userData = users[authUser.uid];
      if (userData) {
        setFirstName(userData.firstName || "");
        setLastName(userData.lastName || "");
        setDisplayName(userData.displayName || "");
        setAuthLevel(userData.authLevel || 1);
        setPin(pinForForm(userData));
        setPhone(getPhoneDisplay(userData));
        setNotificationsEnabled(areNotificationsEnabled());
        setSelectedUserId(authUser.uid);
      }
    }
    // storePinLength participates so the displayed PIN re-pads to the right width
    // once the setting finishes loading.
  }, [authUser, users, storePinLength]);

  const handleUserSelect = (userId) => {
    if (!userId || userId === "") {
      // Reset to current user
      if (authUser && authUser.uid) {
        const userData = users[authUser.uid];
        if (userData) {
          setFirstName(userData.firstName || "");
          setLastName(userData.lastName || "");
          setDisplayName(userData.displayName || "");
          setAuthLevel(userData.authLevel || 1);
          setPin(pinForForm(userData));
          setPhone(getPhoneDisplay(userData));
          setNotificationsEnabled(areNotificationsEnabled());
          setSelectedUserId(authUser.uid);
          setSelectedUserForEdit(null);
        }
      }
      return;
    }
    
    const userData = users[userId];
    if (userData) {
      setFirstName(userData.firstName || "");
      setLastName(userData.lastName || "");
      setDisplayName(userData.displayName || "");
      setAuthLevel(userData.authLevel || 1);
      setPin(pinForForm(userData));
      setPhone(getPhoneDisplay(userData));
      // Only set notifications if it's the current user
      if (userId === authUser?.uid) {
        setNotificationsEnabled(areNotificationsEnabled());
      } else {
        setNotificationsEnabled(userData.notificationsEnabled || false);
      }
      setSelectedUserId(userId);
      setSelectedUserForEdit(userId);
      setEditing(false);
      setCreating(false);
    }
  };

  const handleEditUser = () => {
    const userIdToEdit = selectedUserForEdit || authUser?.uid;
    
    if (!userIdToEdit) {
      notify("Error", "Please select a user to edit");
      return;
    }
    
    const userData = users[userIdToEdit];
    if (!userData) {
      notify("Error", "User data not found");
      return;
    }
    
    setFirstName(userData.firstName || "");
    setLastName(userData.lastName || "");
    setDisplayName(userData.displayName || "");
    setAuthLevel(userData.authLevel || 1);
    setPin(pinForForm(userData));
    setPhone(getPhoneDisplay(userData));
    if (userIdToEdit === authUser?.uid) {
      setNotificationsEnabled(areNotificationsEnabled());
    } else {
      setNotificationsEnabled(userData.notificationsEnabled || false);
    }
    setSelectedUserId(userIdToEdit);
    setEditing(true);
    setCreating(false);
  };

  const handleCreateUser = () => {
    setUserId("");
    setFirstName("");
    setLastName("");
    setDisplayName("");
    setAuthLevel(1);
    setPin("");
    setPhone("");
    setNotificationsEnabled(false);
    setSelectedUserId(null);
    setEditing(false);
    setCreating(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setCreating(false);
    // Reset to selected user or current user data
    const userIdToLoad = selectedUserForEdit || authUser?.uid;
    if (userIdToLoad && users[userIdToLoad]) {
      const userData = users[userIdToLoad];
      setFirstName(userData.firstName || "");
      setLastName(userData.lastName || "");
      setDisplayName(userData.displayName || "");
      setAuthLevel(userData.authLevel || 1);
      setPin(pinForForm(userData));
      setPhone(getPhoneDisplay(userData));
      if (userIdToLoad === authUser?.uid) {
        setNotificationsEnabled(areNotificationsEnabled());
      } else {
        setNotificationsEnabled(userData.notificationsEnabled || false);
      }
      setSelectedUserId(userIdToLoad);
    }
  };

  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      notify("Error", "First name and last name are required");
      return;
    }

    if (authLevel < 1 || authLevel > 5) {
      notify("Error", "Auth level must be between 1 and 5");
      return;
    }

    // Blank on edit means "leave the existing PIN alone"; anything else must be
    // exactly the width this user's role requires.
    if (pin.trim() && pin.trim().length !== requiredPinLength) {
      notify(
        "Error",
        `PIN must be exactly ${requiredPinLength} digits for ${
          Number(authLevel) >= ADMIN_AUTH_LEVEL ? "an admin" : "this user"
        }.`
      );
      return;
    }

    setSaving(true);
    try {
      const tenantId = getTenantId();
      const storeId = getStoreId();
      
      if (!tenantId || !storeId) {
        throw new Error("No tenant/store context");
      }

      const usersPath = getCollectionPath("users");
      
      // Determine user ID
      let finalUserId;
      if (creating) {
        // New user: ID includes first name (sanitized), timestamp, and random for uniqueness
        const sanitizedFirst = firstName.trim()
          .replace(/\s+/g, "_")
          .replace(/[^a-zA-Z0-9_]/g, "")
          .replace(/_+/g, "_")
          .replace(/^_|_$/g, "")
          .slice(0, 30) || "user";
        const timestamp = Date.now();
        const random = Math.random().toString(36).substring(2, 9);
        finalUserId = `user_${sanitizedFirst}_${timestamp}_${random}`;
      } else {
        // For editing, use the selected user ID (from dropdown or current user's UID)
        finalUserId = selectedUserId || selectedUserForEdit || authUser.uid;
      }

      const userRef = doc(db, usersPath, finalUserId);
      
      const phoneDigits = phone.replace(/\D/g, "").slice(0, 10);

      const userData = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        displayName: displayName.trim(),
        authLevel: Number(authLevel),
        notificationsEnabled: notificationsEnabled,
        updatedAt: serverTimestamp(),
      };

      // Stored as a zero-padded STRING, not a number: Number("012") is 12, which
      // then never matched the "012" typed at login. Reads still tolerate the old
      // numeric values via normalizePin().
      if (pin.trim()) {
        userData.pin = normalizePin(pin.trim(), requiredPinLength);
      }

      // Add phone if 10 digits provided (store as string of digits)
      if (phoneDigits.length === 10) {
        userData.phone = phoneDigits;
      }

      // If creating, add createdAt
      if (creating) {
        userData.createdAt = serverTimestamp();
        // For new users, we might want to link to an email_users document
        // For now, just create in users collection
      }

      await setDoc(userRef, userData, { merge: true });

      // Handle notifications toggle (only for current user when editing)
      if (!creating && authUser && authUser.uid && finalUserId === authUser.uid) {
        if (notificationsEnabled && !areNotificationsEnabled()) {
          await registerForNotifications(authUser.uid);
        } else if (!notificationsEnabled && areNotificationsEnabled()) {
          await disableNotifications(authUser.uid);
        }
      }
      
      // Update selectedUserForEdit if we just edited a user
      if (!creating) {
        setSelectedUserForEdit(finalUserId);
      }

      notify("Success", creating ? "User created successfully" : "User updated successfully");
      
      setEditing(false);
      setCreating(false);
      
      // Reset form if creating
      if (creating) {
        setUserId("");
        setFirstName("");
        setLastName("");
        setDisplayName("");
        setAuthLevel(1);
        setPin("");
        setPhone("");
        setNotificationsEnabled(false);
        setSelectedUserId(null);
        setSelectedUserForEdit(null);
      } else {
        // After editing, keep the selected user in the dropdown
        setSelectedUserForEdit(finalUserId);
      }
    } catch (error) {
      console.error("Error saving user:", error);
      notify("Error", `Failed to save user: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleNotificationToggle = async (value) => {
    if (!authUser) return;
    
    setNotificationsEnabled(value);
    
    try {
      if (value) {
        await registerForNotifications(authUser.uid);
      } else {
        await disableNotifications(authUser.uid);
      }
    } catch (error) {
      console.error("Error toggling notifications:", error);
      notify("Error", "Failed to toggle notifications");
      setNotificationsEnabled(!value); // Revert on error
    }
  };

  const handleAuthLevelChange = (delta) => {
    const newValue = Math.max(1, Math.min(5, authLevel + delta));
    setAuthLevel(newValue);
  };

  return (
    <View style={styles.container}>
      <AuthHeader
        title="User Settings"
        isDesktop={isDesktop}
        isAuthenticated={true}
        shopInfo={{}}
      />
      <View style={isDesktop ? styles.desktopWrapper : null}>
        <ScrollView style={styles.scrollView}>
        <View style={styles.content}>
          <Text style={styles.sectionTitle}>User Information</Text>

          {/* PIN length — store-wide. First control on the page because changing
              it invalidates every PIN set by the fields below. */}
          <View style={styles.pinLengthCard}>
            <Text style={styles.fieldLabel}>PIN Length</Text>
            <Text style={styles.pinLengthHelp}>
              How many digits regular users type to sign in ({MIN_PIN_LENGTH}–{MAX_PIN_LENGTH}).
              Admins (auth level {ADMIN_AUTH_LEVEL}) always use {ADMIN_PIN_LENGTH} digits, whatever this is set to.
            </Text>

            {pinLengthLoading ? (
              <ActivityIndicator size="small" color={colors.primary} />
            ) : (
              <>
                <View style={styles.spinnerContainer}>
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[
                      styles.spinnerButton,
                      (!canManagePinLength || pinLengthSaving || pendingPinLength <= MIN_PIN_LENGTH) && styles.buttonDisabled,
                    ]}
                    onPress={() => applyPendingPinLength(pendingPinLength - 1)}
                    disabled={!canManagePinLength || pinLengthSaving || pendingPinLength <= MIN_PIN_LENGTH}
                  >
                    <Text style={styles.spinnerButtonText}>-</Text>
                  </TouchableOpacity>
                  <TextInput
                    style={[
                      styles.spinnerInput,
                      (!canManagePinLength || pinLengthSaving) && styles.inputDisabled,
                    ]}
                    value={pinLengthText}
                    onChangeText={(text) => {
                      const digits = text.replace(/\D/g, "").slice(0, 2);
                      setPinLengthText(digits);
                      const n = Number(digits);
                      if (digits && n >= MIN_PIN_LENGTH && n <= MAX_PIN_LENGTH) {
                        setPendingPinLength(n);
                      }
                    }}
                    // Clamp on the way out, so an out-of-range or empty box snaps
                    // back to something real instead of blocking Apply silently.
                    onBlur={() =>
                      applyPendingPinLength(pinLengthText === "" ? storePinLength : Number(pinLengthText))
                    }
                    keyboardType="numeric"
                    maxLength={2}
                    editable={canManagePinLength && !pinLengthSaving}
                  />
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[
                      styles.spinnerButton,
                      (!canManagePinLength || pinLengthSaving || pendingPinLength >= MAX_PIN_LENGTH) && styles.buttonDisabled,
                    ]}
                    onPress={() => applyPendingPinLength(pendingPinLength + 1)}
                    disabled={!canManagePinLength || pinLengthSaving || pendingPinLength >= MAX_PIN_LENGTH}
                  >
                    <Text style={styles.spinnerButtonText}>+</Text>
                  </TouchableOpacity>
                </View>

                {!canManagePinLength && (() => {
                  // Spell out the block. Being able to edit users is not proof of
                  // admin here — that is ungated for everyone (canEdit is always
                  // true) — so without this the control just looks broken.
                  const who = `${selectedUser?.firstName || ""} ${selectedUser?.lastName || ""}`.trim();
                  const level = selectedUser?.authLevel;
                  return (
                    <Text style={styles.pinLengthHelp}>
                      Currently {storePinLength} digits. Only an admin (auth level {ADMIN_AUTH_LEVEL}) can
                      change this — you are signed in as {who || "an unidentified user"}
                      {level !== undefined ? ` at auth level ${level}` : ""}.
                    </Text>
                  );
                })()}

                {canManagePinLength && pendingPinLength !== storePinLength && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.applyPinLengthButton, pinLengthSaving && styles.buttonDisabled]}
                    onPress={handleApplyPinLength}
                    disabled={pinLengthSaving}
                  >
                    {pinLengthSaving ? (
                      <ActivityIndicator size="small" color={colors.warningInk} />
                    ) : (
                      <>
                        <Text style={styles.applyPinLengthText}>
                          Apply {pendingPinLength}-digit PINs
                        </Text>
                        <Text style={styles.applyPinLengthHint}>
                          Clears regular users' PINs — admins keep theirs
                        </Text>
                      </>
                    )}
                  </TouchableOpacity>
                )}
              </>
            )}
          </View>

          {/* User Dropdown (only for authLevel 5) */}
          {canEdit && (
            <View style={styles.fieldContainer}>
              <Text style={styles.fieldLabel}>Select User to Edit</Text>
              <select
                style={styles.select}
                value={selectedUserForEdit || authUser?.uid || ""}
                onChange={(e) => handleUserSelect(e.target.value)}
                disabled={editing || creating}
              >
                <option value="">-- Select a user --</option>
                {authUser?.uid && users[authUser.uid] && (
                  <option value={authUser.uid}>
                    {`${users[authUser.uid]?.firstName || ''} ${users[authUser.uid]?.lastName || ''}`.trim() || authUser.uid} (Current User)
                  </option>
                )}
                {usersList
                  .filter(user => user.id !== authUser?.uid)
                  .map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
              </select>
            </View>
          )}
          
          {/* First Name */}
          <View style={styles.fieldContainer}>
            <Text style={styles.fieldLabel}>First Name</Text>
            <TextInput
              style={[styles.input, (!editing && !creating) && styles.inputDisabled]}
              value={firstName}
              onChangeText={setFirstName}
              placeholder="Enter first name"
              editable={editing || creating}
            />
          </View>

          {/* Last Name */}
          <View style={styles.fieldContainer}>
            <Text style={styles.fieldLabel}>Last Name</Text>
            <TextInput
              style={[styles.input, (!editing && !creating) && styles.inputDisabled]}
              value={lastName}
              onChangeText={setLastName}
              placeholder="Enter last name"
              editable={editing || creating}
            />
          </View>

          {/* Display Name */}
          <View style={styles.fieldContainer}>
            <Text style={styles.fieldLabel}>Display Name</Text>
            <TextInput
              style={[styles.input, (!editing && !creating) && styles.inputDisabled]}
              value={displayName}
              onChangeText={setDisplayName}
              placeholder="Enter display name"
              editable={editing || creating}
            />
          </View>

          {/* PIN — width follows the edited user's role, not a fixed 3 */}
          <View style={styles.fieldContainer}>
            <Text style={styles.fieldLabel}>PIN ({requiredPinLength} digits)</Text>
            <TextInput
              style={[styles.input, (!editing && !creating) && styles.inputDisabled]}
              value={pin}
              onChangeText={(text) => {
                const cleaned = text.replace(/\D/g, "").substring(0, requiredPinLength);
                setPin(cleaned);
              }}
              placeholder={`Enter ${requiredPinLength}-digit PIN`}
              keyboardType="numeric"
              maxLength={requiredPinLength}
              editable={editing || creating}
            />
            {Number(authLevel) >= ADMIN_AUTH_LEVEL && (
              <Text style={styles.pinLengthHelp}>
                Auth level {ADMIN_AUTH_LEVEL} is an admin, so this PIN is fixed at {ADMIN_PIN_LENGTH} digits.
              </Text>
            )}
          </View>

          {/* Phone (10 digits, formatted as user types) */}
          <View style={styles.fieldContainer}>
            <Text style={styles.fieldLabel}>Phone (10 digits)</Text>
            <TextInput
              style={[styles.input, (!editing && !creating) && styles.inputDisabled]}
              value={phone}
              onChangeText={(text) => {
                const digits = text.replace(/\D/g, "").slice(0, 10);
                setPhone(formatPhoneNumberProgressive(digits));
              }}
              placeholder="(555) 123-4567"
              keyboardType="phone-pad"
              maxLength={14}
              editable={editing || creating}
            />
          </View>

          {/* Auth Level */}
          <View style={styles.fieldContainer}>
            <Text style={styles.fieldLabel}>Auth Level (1-5)</Text>
            <View style={styles.spinnerContainer}>
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[styles.spinnerButton, (!editing && !creating) && styles.buttonDisabled]}
                onPress={() => handleAuthLevelChange(-1)}
                disabled={!editing && !creating}
              >
                <Text style={styles.spinnerButtonText}>-</Text>
              </TouchableOpacity>
              <TextInput
                style={[styles.spinnerInput, (!editing && !creating) && styles.inputDisabled]}
                value={String(authLevel)}
                keyboardType="numeric"
                editable={false}
              />
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[styles.spinnerButton, (!editing && !creating) && styles.buttonDisabled]}
                onPress={() => handleAuthLevelChange(1)}
                disabled={!editing && !creating}
              >
                <Text style={styles.spinnerButtonText}>+</Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Action Buttons */}
          <View style={styles.buttonContainer}>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[styles.button, styles.editButton, (!canEdit || creating || editing || (!selectedUserForEdit && !authUser?.uid)) && styles.buttonDisabled]}
              onPress={handleEditUser}
              disabled={!canEdit || editing || creating || (!selectedUserForEdit && !authUser?.uid)}
            >
              <Text style={styles.buttonText}>Edit User</Text>
            </TouchableOpacity>

            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={[styles.button, styles.createButton, (!canEdit || creating || editing) && styles.buttonDisabled]}
              onPress={handleCreateUser}
              disabled={!canEdit || editing || creating}
            >
              <Text style={styles.buttonText}>Create User</Text>
            </TouchableOpacity>
          </View>

          {/* Save/Cancel Buttons (shown when editing or creating) */}
          {(editing || creating) && (() => {
            // Check if all required fields are filled (for create mode)
            const allFieldsFilled = creating
              ? firstName.trim() && lastName.trim() && pin.length === requiredPinLength
              : pin.length === 0 || pin.length === requiredPinLength; // Edit: blank keeps the existing PIN
            
            return (
              <View style={styles.buttonContainer}>
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.cancelButton]}
                  onPress={handleCancel}
                  disabled={saving}
                >
                  <Text style={styles.buttonText}>Cancel</Text>
                </TouchableOpacity>

                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.button, styles.saveButton, (!allFieldsFilled || saving) && styles.buttonDisabled]}
                  onPress={handleSave}
                  disabled={!allFieldsFilled || saving}
                >
                  {saving ? (
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  ) : (
                    <Text style={styles.buttonText}>Save</Text>
                  )}
                </TouchableOpacity>
              </View>
            );
          })()}
        </View>
      </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F6F8F7",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
  },
  desktopWrapper: {
    width: "100%",
    maxWidth: 800,
    flex: 1,
    alignSelf: "center",
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 20,
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 20,
  },
  fieldContainer: {
    marginBottom: 20,
  },
  pinLengthCard: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: radius.md,
    padding: spacing.lg,
    marginBottom: spacing.xl,
    boxShadow: elevation.sm,
    elevation: 2,
  },
  pinLengthHelp: {
    fontSize: 13,
    color: colors.ink500,
    lineHeight: 18,
    marginTop: spacing.xs,
    marginBottom: spacing.md,
  },
  // Warning rather than danger: destructive to PINs, but it is a deliberate
  // configuration step, not a delete button.
  applyPinLengthButton: {
    marginTop: spacing.lg,
    backgroundColor: colors.warningSoft,
    borderWidth: 1,
    borderColor: colors.warning,
    borderRadius: radius.md,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    alignItems: "center",
    justifyContent: "center",
  },
  applyPinLengthText: {
    color: colors.warningInk,
    fontSize: 15,
    fontWeight: "700",
  },
  applyPinLengthHint: {
    color: colors.warningInk,
    fontSize: 11,
    marginTop: spacing.xs / 2,
    opacity: 0.8,
  },
  fieldRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  fieldIcon: {
    fontSize: 20,
    marginRight: 12,
  },
  fieldLabel: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 8,
  },
  input: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    padding: 12,
    fontSize: 16,
    color: "#2E3A38",
  },
  inputDisabled: {
    backgroundColor: "#EDF1F0",
    color: "#8A9794",
  },
  spinnerContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  spinnerButton: {
    width: 40,
    height: 40,
    backgroundColor: "#0E7C6B",
    borderRadius: 6,
    justifyContent: "center",
    alignItems: "center",
  },
  spinnerButtonText: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#FFFFFF",
  },
  spinnerInput: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    padding: 12,
    fontSize: 16,
    textAlign: "center",
    color: "#2E3A38",
  },
  select: {
    width: "100%",
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    color: "#2E3A38",
    minHeight: 44,
  },
  buttonContainer: {
    flexDirection: "row",
    gap: 12,
    marginTop: 20,
  },
  button: {
    flex: 1,
    padding: 16,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  editButton: {
    backgroundColor: "#0E7C6B",
  },
  createButton: {
    backgroundColor: "#1F8A5B",
  },
  saveButton: {
    backgroundColor: "#0E7C6B",
  },
  cancelButton: {
    backgroundColor: "#C4453D",
  },
  buttonDisabled: {
    backgroundColor: "#DDE3E2",
    opacity: 0.5,
  },
  buttonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
});

export default UserSettings;

